import type { ReactNode } from "react";

/**
 * Two stacked views of the whole system, shown before the playable request-flow map:
 *
 * 1. "전체 시스템 아키텍처" - the conventional IT diagram (user-requested, 2026-08-15):
 *    runtime stack top-to-bottom (SPA -> FastAPI with its four internal engines -> DB)
 *    plus two side strips for the offline ML pipeline and CI/CD. Box labels are the
 *    actual repo folder/tech names, verified: apps/decision_api, safefds/{features,
 *    predictors,policy,governance,persistence}, web/ React 18 + TS, python:3.12 image.
 * 2. "시스템 조감도" - the same three layers by ROLE in FDS terms (factory makes and
 *    tests the decision standard, store applies it in real time, logistics re-tests and
 *    delivers every change), each with a one-line role sentence per user feedback.
 *
 * Deliberately NOT another request-flow diagram - ArchitecturePlayground (right below)
 * already animates one transfer's path through the serving layer.
 *
 * Accuracy contract (every box checked against the repo, not a marketing diagram):
 * - factory steps mirror the actual module order in ml/ (generate_data -> baseline_rule
 *   -> train -> evaluate/gate -> ceiling_analysis -> evaluate_v2 -> certify_v3);
 * - the factory ships JSON artifacts (ml/artifacts/*.json), never code: the model file
 *   carries its own SHA-256 (checked at load, safefds/predictors/ml.py) and the
 *   certificate records checksums of model/feature/policy/dataset/config;
 * - store order mirrors decide_transaction.py, same as the playground's nodes;
 * - logistics: CI re-verifies on every push and Render auto-deploys on every commit -
 *   these are both push-triggered; we do NOT claim CI gates the deploy, because it
 *   doesn't (autoDeployTrigger: commit).
 */

interface Step {
  label: string;
  tech: string;
}

const FACTORY: Step[] = [
  { label: "데이터 생성", tech: "generate_data" },
  { label: "규칙 기준선", tech: "baseline_rule" },
  { label: "학습", tech: "train" },
  { label: "평가·적용 게이트", tech: "evaluate" },
  { label: "상한 분석", tech: "ceiling_analysis" },
  { label: "자동화 측정", tech: "evaluate_v2" },
  { label: "인증서 발급", tech: "certify_v3" },
];

const STORE_BEFORE_BRANCH: Step[] = [
  { label: "브라우저", tech: "Public Demo UI" },
  { label: "문", tech: "FastAPI" },
  { label: "단서 모으기", tech: "Feature Engine" },
  { label: "위험 점수", tech: "Predictor" },
  { label: "판정", tech: "Policy Engine" },
];

const LOGISTICS: Step[] = [
  { label: "자동 검사", tech: "GitHub Actions CI" },
  { label: "포장", tech: "Docker 빌드" },
  { label: "자동 배달", tech: "Render — 커밋마다 배포" },
];

function Flow({ steps, tail }: { steps: Step[]; tail?: ReactNode }) {
  return (
    <div className="sysmap-flow">
      {steps.map((step, i) => (
        <span key={step.label} style={{ display: "contents" }}>
          {i > 0 && (
            <span className="sysmap-arrow" aria-hidden="true">
              →
            </span>
          )}
          <span className="sysmap-step">
            {step.label}
            <span className="sysmap-step-tech">{step.tech}</span>
          </span>
        </span>
      ))}
      {tail}
    </div>
  );
}

export function SystemMap() {
  return (
    <>
      <section className="card" id="tech-architecture">
        <h2>🧩 전체 시스템 아키텍처</h2>
        <p className="muted">
          기술 관점 한 장 요약 — 상자의 이름은 실제 저장소의 폴더·기술명 그대로입니다.
        </p>
        <div className="techarch">
          <div className="techarch-box">
            🖥️ 브라우저 — React 18 + TypeScript SPA
            <span className="techarch-tech">web/</span>
          </div>
          <div className="techarch-arrow" aria-hidden="true">
            ↓ REST API (HTTPS)
          </div>
          <div className="techarch-group">
            <div className="techarch-group-title">
              ⚡ API 서버 — FastAPI · Python 3.12
              <span className="techarch-tech">apps/decision_api + safefds/</span>
            </div>
            <div className="techarch-inner">
              <div className="techarch-box">
                Feature Engine
                <span className="techarch-tech">JIT 피처 계산 · features/</span>
              </div>
              <div className="techarch-box">
                Predictor
                <span className="techarch-tech">인프로세스 ML 서빙 · predictors/</span>
              </div>
              <div className="techarch-box">
                Policy Engine
                <span className="techarch-tech">3밴드 판정 · policy/</span>
              </div>
              <div className="techarch-box">
                Runtime Governor
                <span className="techarch-tech">자동화 강등 · governance/</span>
              </div>
            </div>
          </div>
          <div className="techarch-arrow" aria-hidden="true">
            ↓ Unit of Work — 단일 트랜잭션 (persistence/)
          </div>
          <div className="techarch-box">
            🗄️ PostgreSQL
            <span className="techarch-tech">판정 · 검토 케이스 · 감사 기록 · Outbox</span>
          </div>
        </div>
        <div className="techarch-strip">
          🧪 <strong>오프라인 ML 파이프라인</strong>{" "}
          <span className="techarch-tech">ml/</span> → 봉인된 산출물 JSON{" "}
          <span className="techarch-tech">ml/artifacts/ · SHA-256</span> → 서버 시작 시
          로드·검증되어 Predictor로
        </div>
        <div className="techarch-strip">
          🔁 <strong>CI/CD</strong> — GitHub Actions → Docker 멀티스테이지 → Render{" "}
          <span className="techarch-tech">커밋마다 자동</span>
        </div>
      </section>

      <section className="card" id="system-map">
        <h2>🛰️ 시스템 조감도 — 공장 · 매장 · 물류</h2>
        <p>
          위 아키텍처를 <strong>FDS 관점의 역할</strong>로 묶으면 딱 세 층이에요.
        </p>

        <div className="sysmap">
          <div className="sysmap-layer">
            <div className="sysmap-layer-head">
              <span className="sysmap-layer-title">🏭 ① 공장 — 모델을 만들고 시험하는 곳</span>
              <span className="sysmap-layer-tech">오프라인 ML 파이프라인 (ml/)</span>
            </div>
            <p className="sysmap-role">
              FDS에서의 역할: <strong>어떤 송금을 의심할지 &quot;판정 기준&quot;을 만들고
              시험하는 곳.</strong> 여기서 시험에 떨어지면 매장에 들어가지 못합니다.
            </p>
            <Flow steps={FACTORY} />
            <p className="sysmap-note">
              공장이 매장에 보내는 건 코드가 아니라 <strong>봉인된 JSON 산출물</strong>{" "}
              (<code>ml/artifacts/</code>) — 모든 파일에 지문(SHA-256 체크섬)이 찍혀 있어요.
            </p>
          </div>

          <div className="sysmap-seal">
            <span className="sysmap-seal-icon" aria-hidden="true">
              📦⬇️
            </span>
            <p>
              <strong>제일 중요한 화살표.</strong> 봉인된 지문이{" "}
              <strong>서버의 실체와 하나라도 다르면 인증서가 무효 → 자동화 강등.</strong>{" "}
              바꿔치기가 조용히 지나갈 길이 없어요.
            </p>
          </div>

          <div className="sysmap-layer">
            <div className="sysmap-layer-head">
              <span className="sysmap-layer-title">🏪 ② 매장 — 송금을 실시간으로 판정하는 곳</span>
              <span className="sysmap-layer-tech">온라인 서빙 (safefds/, apps/)</span>
            </div>
            <p className="sysmap-role">
              FDS에서의 역할: <strong>송금이 들어온 그 순간, 공장이 만든 기준으로 실시간
              판정하는 곳.</strong> 1건을 1,000분의 1초 수준으로 승인 / 자동 본인확인 /
              사람 검토 중 하나로 보냅니다.
            </p>
            <Flow
              steps={STORE_BEFORE_BRANCH}
              tail={
                <>
                  <span className="sysmap-arrow" aria-hidden="true">
                    →
                  </span>
                  <span className="sysmap-branch">
                    <span className="sysmap-step">
                      자동 본인확인
                      <span className="sysmap-step-tech">STEP_UP</span>
                    </span>
                    <span className="sysmap-branch-or" aria-hidden="true">
                      |
                    </span>
                    <span className="sysmap-step">
                      면허 정지 시 사람에게
                      <span className="sysmap-step-tech">Governor → REVIEW</span>
                    </span>
                  </span>
                  <span className="sysmap-arrow" aria-hidden="true">
                    →
                  </span>
                  <span className="sysmap-step">
                    원자 저장
                    <span className="sysmap-step-tech">PostgreSQL 단일 트랜잭션</span>
                  </span>
                </>
              }
            />
          </div>

          <div className="sysmap-layer">
            <div className="sysmap-layer-head">
              <span className="sysmap-layer-title">📦 ③ 물류 — 매 푸시마다 검사하고 배달하는 곳</span>
              <span className="sysmap-layer-tech">품질·배포 파이프라인</span>
            </div>
            <p className="sysmap-role">
              FDS에서의 역할: <strong>공장·매장의 코드가 바뀔 때마다 전체를 다시 검사하고
              새 버전을 배달하는 곳.</strong> 판정 로직이 테스트를 건너뛴 채 바뀌는 일이
              없게요.
            </p>
            <Flow steps={LOGISTICS} />
          </div>
        </div>

        <p className="muted">송금 한 건이 매장 안을 지나가는 길은, 바로 아래에서 직접 재생해 보세요. 👇</p>
      </section>
    </>
  );
}
